import { ChevronRightIcon, CubeIcon } from '@heroicons/react/24/outline';
import type { DataTableColumn } from '@odo/shared-ui';
import { CategoryChip } from '../CategoryChip';
import {
    applyRules,
    rulesForVersion,
    type PricedProduct,
    type PricedVersion
} from '../../../services/practiceProductPricing';
import { formatMoneyCents as money } from '../../../utils/money';
import { PricingBreakdownPanel } from './PricingBreakdownPanel';

// One table row: a resolved product, or one version of an unresolved product
// (those render a priced row per version - the admin picks at charge time).
export interface PricingDisplayRow {
    rowId: string;
    product: PricedProduct;
    version: PricedVersion | null;
}

export function toDisplayRows(products: PricedProduct[]): PricingDisplayRow[] {
    return products.flatMap((product): PricingDisplayRow[] => {
        if (product.resolvedVersionId === null && product.versions.length > 0) {
            return product.versions.map((version) => ({
                rowId: `${product.id}:${version.id}`,
                product,
                version
            }));
        }
        return [{ rowId: product.id, product, version: null }];
    });
}

// Stacked rules collapse to one number here: the total off the list price.
// The per-rule walk lives in the expandable breakdown panel.
function totalOffCents(product: PricedProduct): number {
    if (product.defaultPriceCents === null || product.effectivePriceCents === null) return 0;
    return Math.max(0, product.defaultPriceCents - product.effectivePriceCents);
}

interface RowPricing {
    listCents: number | null;
    effectiveCents: number | null;
    offCents: number;
}

function rowPricing(row: PricingDisplayRow): RowPricing {
    if (row.version) {
        const applied = rulesForVersion(row.product.rules, row.version.id);
        const effectiveCents = applyRules(row.version.priceCents, applied);
        return {
            listCents: row.version.priceCents,
            effectiveCents,
            offCents: row.version.priceCents - effectiveCents
        };
    }
    return {
        listCents: row.product.defaultPriceCents,
        effectiveCents: row.product.effectivePriceCents,
        offCents: totalOffCents(row.product)
    };
}

interface BuildPricingColumnsArgs {
    expandedRowId: string | null;
    practiceName: string;
}

// Plain functions, not components: a "*Columns.tsx" file's only component
// export is column-preset render output (see practiceColumns.tsx's convention).
//
// The breakdown panel (was a `colSpan` row) renders inside the Product
// column's own cell instead - DataTable renders exactly one `<td>` per column
// per row, with no row-spanning-row escape hatch, so a full-width insert
// isn't expressible. Capped at `max-w-md` in the panel itself so an expanded
// row doesn't drag every other row's Product column wider (`tableLayout`
// "auto" sizes one column off the widest cell across the whole table).
export function buildPricingColumns({
    expandedRowId,
    practiceName
}: BuildPricingColumnsArgs): DataTableColumn<PricingDisplayRow>[] {
    return [
        {
            key: 'product',
            label: 'Product',
            width: 'auto',
            cellClassName: 'pl-6 pr-3 py-3 align-top',
            headerClassName: 'whitespace-nowrap !px-3 !pl-6',
            render: (row) => {
                const { product, version } = row;
                const isExpanded = row.rowId === expandedRowId;
                const resolvedLabel = version
                    ? version.label
                    : (product.versions.find((v) => v.id === product.resolvedVersionId)?.label ??
                      null);
                return (
                    <div>
                        <div className="flex items-center gap-3">
                            <span className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                                <CubeIcon className="h-3.5 w-3.5" />
                            </span>
                            <span
                                className="text-sm font-medium text-gray-800 truncate"
                                title={product.name}
                            >
                                {product.name}
                            </span>
                            {/* Which version this practice gets, when the product has several. */}
                            {(version || product.versions.length > 1) && resolvedLabel && (
                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0">
                                    {resolvedLabel}
                                </span>
                            )}
                        </div>
                        {isExpanded && (
                            <PricingBreakdownPanel
                                product={product}
                                practiceName={practiceName}
                                version={version ?? undefined}
                            />
                        )}
                    </div>
                );
            }
        },
        {
            key: 'category',
            label: 'Category',
            width: 'auto',
            cellClassName: 'px-3 align-top whitespace-nowrap',
            headerClassName: 'whitespace-nowrap !px-3',
            render: (row) => <CategoryChip category={row.product.category} />
        },
        {
            key: 'listPrice',
            label: 'List price',
            width: 'auto',
            align: 'right',
            cellClassName: 'px-3 align-top whitespace-nowrap',
            headerClassName: 'whitespace-nowrap !px-3',
            render: (row) => {
                const { listCents, offCents } = rowPricing(row);
                const hasDiscount = offCents > 0;
                return (
                    <span
                        className={`text-sm tabular-nums ${listCents === null ? 'text-gray-300' : hasDiscount ? 'text-gray-400 line-through' : 'text-gray-700'}`}
                    >
                        {listCents === null ? '-' : money(listCents)}
                    </span>
                );
            }
        },
        {
            key: 'discount',
            label: 'Discount',
            width: 'auto',
            cellClassName: 'px-3 align-top whitespace-nowrap',
            headerClassName: 'whitespace-nowrap !px-3',
            render: (row) => {
                const { offCents } = rowPricing(row);
                return offCents > 0 ? (
                    <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-md bg-green-50 text-green-600 tabular-nums">
                        -{money(offCents)}
                    </span>
                ) : (
                    <span className="text-gray-300">-</span>
                );
            }
        },
        {
            key: 'effective',
            label: 'Effective',
            width: 'auto',
            align: 'right',
            cellClassName: 'px-3 align-top whitespace-nowrap',
            headerClassName: 'whitespace-nowrap !px-3',
            render: (row) => {
                const { effectiveCents } = rowPricing(row);
                return (
                    <span
                        className={`text-sm font-semibold tabular-nums ${effectiveCents === null ? 'text-gray-300' : 'text-gray-900'}`}
                    >
                        {effectiveCents === null ? '-' : money(effectiveCents)}
                    </span>
                );
            }
        },
        {
            key: 'chevron',
            label: '',
            width: 'auto',
            cellClassName: 'px-3 align-top w-10 text-right',
            headerClassName: 'whitespace-nowrap !px-3',
            render: (row) => (
                <ChevronRightIcon
                    className={`h-4 w-4 inline text-gray-300 transition-transform ${row.rowId === expandedRowId ? 'rotate-90' : ''}`}
                />
            )
        }
    ];
}
